import React, { useState, useEffect } from 'react';
import { TYPES_BIEN_BY_TRANSACTION, ACTIVITY_TYPES_BY_BIEN_TYPE_MAPPING } from '../constants/propertyMappings';

// Composants
import NavigationBar from '../components/contact-form/NavigationBar';
import { isContactAcquireur, syncContactTagsByType } from '../utils/contactProfile';
import IdentitySection from '../components/contact-form/IdentitySection';
import SearchSection from '../components/contact-form/SearchSection';
import MultipleSearchesManager from '../components/contact-form/MultipleSearchesManager';
import FinancialSection from '../components/contact-form/FinancialSection';
import PropertiesSection from '../components/contact-form/PropertiesSection';
import HistoriqueSection from '../components/contact-form/HistoriqueSection';
import AIWidgets from '../components/contact-form/ai-widgets/AIWidgets';
import EmailGeneratorModal from '../components/EmailGeneratorModal';
import ActionsBar from '../components/contact-form/ActionsBar';

const ContactForm = ({ contact, onSave, onCancel, onView, onContactUpdate, userProfile, isNew }) => {
  // États du formulaire
  const [formData, setFormData] = useState({
    type_contact: '',
    titre: '',
    nom: '',
    prenom: '',
    nom_societe: '',
    profession: '',
    telephones: [{ type: 'Mobile', numero: '', principal: true }],
    emails: [{ type: 'Pro', email: '', principal: true }],
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'France',
    negociateur_id: '',
    groupes: [],
    origine: '',
    langues: [],
    preference_communication: '',
    statut_lead: 'Nouveau',
    date_prochain_rappel: '',
    // tags: champ UI dérivé (= groupes hors-grille). Plus stocké en state séparé.
    criteres_recherche: {
      operation: null, // Aligné taxonomie: cession, location, location_gerance, vente_immo
      profil: null, // Profil de bien: chr, retail, bureaux, activite_logistique
      budget_min: null,
      budget_max: null,
      surface_min: null,
      surface_max: null,
      types_biens: [],
      types_activites: [],
      equipements_souhaites: null, // Objet dynamique selon le profil
      villes: [],
      secteurs: [],
      notes: ''
    },
    informations_confidentielles: {
      apport_disponible: null,
      capacite_emprunt: null,
      revenus_annuels: null,
      banque: '',
      nom_banquier: ''
    }
  });

  // États pour l'autocomplétion d'adresse
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  
  // État pour la photo de profil
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loadedContactId, setLoadedContactId] = useState(null); // Suivre l'ID du contact chargé
  
  // État pour le résumé IA
  const [aiSummary, setAiSummary] = useState(null);
  
  // État pour la modale d'email
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // État pour déclencher le refresh de l'historique
  const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);

  // Fonction pour recharger les données du contact depuis l'API
  const reloadContactData = async () => {
    if (!contact?.id) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}`);
      const updatedContact = await response.json();
      
      // Mettre à jour le résumé IA si présent
      if (updatedContact.resume_ia?.texte) {
        setAiSummary(updatedContact.resume_ia.texte);
      }
      
      // Mettre à jour la photo si présente
      if (updatedContact.photo_url) {
        setProfilePhoto(`${API_URL}${updatedContact.photo_url}`);
      } else {
        setProfilePhoto(null);
      }
      
      // Mettre à jour les données du formulaire pour l'historique
      setFormData(prevData => ({
        ...prevData,
        historique_communications: updatedContact.historique_communications || []
      }));
      
      // CRITIQUE: Notifier le parent (App.js) pour mettre à jour l'état global
      if (onContactUpdate) {
        onContactUpdate(updatedContact);
      }
      
      // Déclencher le refresh de l'historique
      setHistoryRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error reloading contact data:', error);
    }
  };

  // Charger les données du contact si en mode édition
  useEffect(() => {
    if (contact) {
      const defaultCriteres = {
        operation: null, // cession, location, location_gerance, vente_immo
        profil: null, // chr, retail, bureaux, activite_logistique
        budget_min: null,
        budget_max: null,
        surface_min: null,
        surface_max: null,
        types_biens: [],
        types_activites: [],
        equipements_souhaites: null,
        villes: [],
        secteurs: [],
        notes: ''
      };
      
      const defaultConfidentielles = {
        apport_disponible: null,
        capacite_emprunt: null,
        revenus_annuels: null,
        banque: '',
        nom_banquier: ''
      };
      
      // Single source of truth : on stocke uniquement dans `groupes`.
      // Le champ `tags` du formulaire est dérivé en lecture (= groupes hors availableGroups).
      // Cela élimine le bug récurrent où décocher un groupe le faisait réapparaître via tags.
      setFormData({
        ...contact,
        groupes: contact.groupes || [],
        criteres_recherche: contact.criteres_recherche || defaultCriteres,
        recherches: contact.recherches || [], // Support multiples recherches
        informations_confidentielles: contact.informations_confidentielles || defaultConfidentielles
      });
      
      // Charger le résumé IA sauvegardé
      if (contact.resume_ia?.texte) {
        setAiSummary(contact.resume_ia.texte);
      }
      
      // Charger la photo de profil sauvegardée SEULEMENT lors du premier chargement
      // ou si l'ID du contact a changé (nouveau contact)
      console.log('🔄 ContactForm useEffect triggered - contact changed:', contact?.id, 'photo_url:', contact?.photo_url, 'loadedContactId:', loadedContactId);
      
      // Charger la photo SEULEMENT si c'est un nouveau contact (ID différent)
      if (contact.id !== loadedContactId) {
        console.log('📸 New contact detected, loading photo');
        setLoadedContactId(contact.id);
        
        if (contact.photo_url) {
          const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
          const photoUrl = `${API_URL}${contact.photo_url}`;
          console.log('📸 Setting profilePhoto to:', photoUrl);
          setProfilePhoto(photoUrl);
        } else {
          console.log('📸 No photo_url, setting profilePhoto to null');
          setProfilePhoto(null);
        }
      } else {
        // Même contact mais photo_url a peut-être changé (après upload)
        // On met à jour SEULEMENT si contact.photo_url existe et est différent
        if (contact.photo_url && !profilePhoto) {
          const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
          const photoUrl = `${API_URL}${contact.photo_url}`;
          console.log('📸 Photo available but not displayed, loading it:', photoUrl);
          setProfilePhoto(photoUrl);
        } else {
          console.log('📸 Same contact, keeping current photo state');
        }
      }
    } else if (userProfile) {
      // Nouveau contact : utiliser le profil utilisateur comme négociateur par défaut
      setFormData(prev => ({
        ...prev,
        negociateur_id: userProfile.id || userProfile.userId || '',
      }));
    }
  }, [contact, userProfile]);

  // ==================== HANDLERS ====================
  
  // Handler pour l'upload de photo
  const handlePhotoUpload = async (file) => {
    if (!contact?.id) {
      alert('⚠️ Veuillez d\'abord enregistrer le contact avant d\'ajouter une photo');
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/upload-photo`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('📤 Photo upload response:', data);
        const photoUrl = `${API_URL}${data.photo_url}`;
        console.log('📸 Setting profilePhoto after upload to:', photoUrl);
        setProfilePhoto(photoUrl);
        
        // Mettre à jour l'état parent directement sans recharger tout le contact
        if (onContactUpdate && contact) {
          const updatedContact = { ...contact, photo_url: data.photo_url };
          console.log('🔄 Calling onContactUpdate with:', updatedContact.id, 'photo_url:', updatedContact.photo_url);
          onContactUpdate(updatedContact);
        }
        
        alert('✅ Photo enregistrée avec succès !');
      } else {
        const errorData = await response.json();
        console.error('Upload error:', errorData);
        alert(`❌ Erreur : ${errorData.detail || 'Upload failed'}`);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert(`❌ Erreur lors de l'upload : ${error.message}`);
    }
  };

  // Handler pour supprimer la photo
  const handlePhotoDelete = async () => {
    if (!contact?.id) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/contacts/${contact.id}/photo`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setProfilePhoto(null);
        
        // Mettre à jour l'état parent directement
        if (onContactUpdate && contact) {
          const updatedContact = { ...contact, photo_url: null };
          onContactUpdate(updatedContact);
        }
        
        alert('✅ Photo supprimée avec succès !');
      } else {
        const errorData = await response.json();
        console.error('Delete error:', errorData);
        alert(`❌ Erreur : ${errorData.detail || 'Delete failed'}`);
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert(`❌ Erreur lors de la suppression : ${error.message}`);
    }
  };

  
  // Handler générique pour les champs simples
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handler pour les critères de recherche
  const handleCritereChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      criteres_recherche: {
        ...prev.criteres_recherche,
        [field]: value
      }
    }));
  };

  // Handler pour les informations confidentielles
  const handleConfidentielChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      informations_confidentielles: {
        ...prev.informations_confidentielles,
        [field]: value
      }
    }));
  };

  // Handler pour le changement de type de transaction
  const handleTransactionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      criteres_recherche: {
        ...prev.criteres_recherche,
        type_transaction: value,
        types_biens: [],
        types_activites: []
      }
    }));
  };

  // ==================== TYPES DE BIENS & ACTIVITÉS ====================
  
  const getFilteredTypesBiens = () => {
    const transaction = formData.criteres_recherche?.type_transaction || 'Vente';
    return TYPES_BIEN_BY_TRANSACTION[transaction] || [];
  };

  const getFilteredActivites = () => {
    const selectedBiens = formData.criteres_recherche?.types_biens || [];
    if (selectedBiens.length === 0) return [];
    
    const allActivites = new Set();
    selectedBiens.forEach(bien => {
      const activites = ACTIVITY_TYPES_BY_BIEN_TYPE_MAPPING[bien] || [];
      activites.forEach(act => allActivites.add(act));
    });
    
    return Array.from(allActivites);
  };

  const addTypeBien = (bien) => {
    const currentBiens = formData.criteres_recherche?.types_biens || [];
    if (bien && !currentBiens.includes(bien)) {
      handleCritereChange('types_biens', [...currentBiens, bien]);
    }
  };

  const removeTypeBien = (bien) => {
    const currentBiens = formData.criteres_recherche?.types_biens || [];
    const newBiens = currentBiens.filter(b => b !== bien);
    setFormData(prev => ({
      ...prev,
      criteres_recherche: {
        ...prev.criteres_recherche,
        types_biens: newBiens,
        types_activites: []
      }
    }));
  };

  const addTypeActivite = (activite) => {
    const currentActivites = formData.criteres_recherche?.types_activites || [];
    if (activite && !currentActivites.includes(activite)) {
      handleCritereChange('types_activites', [...currentActivites, activite]);
    }
  };

  const removeTypeActivite = (activite) => {
    const currentActivites = formData.criteres_recherche?.types_activites || [];
    handleCritereChange('types_activites', currentActivites.filter(a => a !== activite));
  };

  // Handler pour toggler un équipement souhaité
  const toggleEquipement = (equipId) => {
    const currentEquipements = formData.criteres_recherche?.equipements_souhaites || {};
    const newEquipements = { ...currentEquipements };
    
    if (newEquipements[equipId]) {
      delete newEquipements[equipId];
    } else {
      newEquipements[equipId] = true;
    }
    
    // Si plus aucun équipement, mettre null
    const hasEquipements = Object.keys(newEquipements).length > 0;
    handleCritereChange('equipements_souhaites', hasEquipements ? newEquipements : null);
  };

  // Handler pour mettre à jour les recherches multiples
  const handleUpdateRecherches = (updatedRecherches) => {
    setFormData(prev => ({
      ...prev,
      recherches: updatedRecherches
    }));
  };

  // ==================== AUTOCOMPLÉTION ADRESSE ====================
  
  const searchAddress = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=8`
      );
      const data = await response.json();
      
      let suggestions = data.features || [];
      
      if (query.toLowerCase().includes('monaco')) {
        const monacoAddresses = [
          { properties: { label: "Avenue des Beaux-Arts, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Boulevard Albert Ier, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Avenue Saint-Martin, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Rue Grimaldi, 98000 Monaco", city: "Monaco", postcode: "98000" }},
          { properties: { label: "Boulevard du Larvotto, 98000 Monaco", city: "Monaco", postcode: "98000" }}
        ];
        suggestions = [...monacoAddresses, ...suggestions];
      }

      setAddressSuggestions(suggestions.slice(0, 8));
      setShowAddressSuggestions(true);
    } catch (error) {
      console.error("Erreur lors de la recherche d'adresse:", error);
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
    }
  };

  const selectAddress = (suggestion) => {
    const props = suggestion.properties;
    handleChange('adresse', props.name || props.label || '');
    handleChange('code_postal', props.postcode || '');
    handleChange('ville', props.city || '');
    setShowAddressSuggestions(false);
  };

  // ==================== CALCULS IA ====================
  
  const calculateRatioEndettement = () => {
    const revenus = parseFloat(formData.informations_confidentielles?.revenus_annuels) || 0;
    if (revenus === 0) return null;
    
    const capacite = parseFloat(formData.informations_confidentielles?.capacite_emprunt) || 0;
    const mensualite = (capacite * 0.04) / 12;
    const revenuMensuel = revenus / 12;
    const ratio = (mensualite / revenuMensuel) * 100;
    
    return Math.round(ratio);
  };

  // Calcul du score de faisabilité
  // Retourne null si AUCUN champ qualifiant n'est rempli, pour éviter
  // l'affichage trompeur "50/100 À consolider" sur un contact vide.
  const calculateScoreFaisabilite = () => {
    const apport = parseFloat(formData.informations_confidentielles?.apport_disponible) || 0;
    const capacite = parseFloat(formData.informations_confidentielles?.capacite_emprunt) || 0;
    const hasFinances = apport > 0 && capacite > 0;
    const hasBudget = !!formData.criteres_recherche?.budget_max;
    const hasTypes = (formData.criteres_recherche?.types_biens || []).length > 0;
    const hasVilles = (formData.criteres_recherche?.villes || []).length > 0;
    if (!hasFinances && !hasBudget && !hasTypes && !hasVilles) return null;
    let score = 50;
    if (hasFinances) score += 20;
    if (hasBudget) score += 10;
    if (hasTypes) score += 10;
    if (hasVilles) score += 10;
    return Math.min(score, 100);
  };

  const generateResume = () => {
    const nom = `${formData.prenom || ''} ${formData.nom || 'Contact'}`.trim();
    const profession = formData.profession ? `, ${formData.profession}` : '';
    const types = (formData.criteres_recherche?.types_biens || []).join(' ou ');
    const villes = (formData.criteres_recherche?.villes || []).join(', ');
    const budget = formData.criteres_recherche?.budget_max ? `${formData.criteres_recherche.budget_max}€` : 'à définir';
    
    return `"${nom}${profession}, recherche ${types || 'un bien'} ${villes ? `à ${villes}` : ''} (budget: ${budget}). ${formData.informations_confidentielles?.apport_disponible ? 'Financement validé.' : 'En cours de validation financière.'}"`;
  };

  // ==================== SOUMISSION ====================
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // groupes est la single source of truth : pas de fusion nécessaire avec tags.
    // Les tags personnalisés sont déjà dans groupes (cf. addTag dans IdentitySection).
    let dataToSave = {
      ...formData,
      groupes: [...new Set(formData.groupes || [])], // Dédup au cas où
      // Conserver le resume_ia existant ou le créer avec l'aiSummary actuel
      resume_ia: aiSummary ? {
        texte: aiSummary,
        date_generation: contact?.resume_ia?.date_generation || new Date().toISOString()
      } : contact?.resume_ia || null
    };
    
    // Synchroniser automatiquement les tags "Acquéreurs"/"Propriétaires" selon le type
    // (ex: changement de type d'un Propriétaire vers Acquéreur/Propriétaire ajoute le tag manquant)
    dataToSave = syncContactTagsByType(dataToSave);
    
    // Le champ tags du formulaire est purement UI (dérivé), jamais envoyé au backend
    delete dataToSave.tags;
    
    onSave(dataToSave);
  };

  // ==================== VALEURS CALCULÉES ====================
  
  // lot DK2 — une entree de la barre n'existe que si sa section est affichee
  const sectionsVisibles = [
    { id: 'general', label: 'Identité' },
    ...(isContactAcquireur(formData) ? [
      { id: 'recherches', label: 'Recherche' },
      { id: 'confidentiel', label: 'Financier' },
      ...(isNew ? [] : [{ id: 'biens', label: 'Biens' }]),
    ] : []),
    ...(isNew ? [] : [{ id: 'historique', label: 'Historique' }]),
  ];

  const score = calculateScoreFaisabilite();
  const ratio = calculateRatioEndettement();
  const displayName = `${formData.prenom || ''} ${formData.nom || 'Nouveau contact'}`.trim();
  const resumeFallback = generateResume();
  // lot DK2 — aucun resume invente : le repli n'apparait qu'une fois le contact enregistre
  const resume = aiSummary || (contact?.id ? resumeFallback : '');
  const hasApport = !!formData.informations_confidentielles?.apport_disponible;
  const hasBudget = !!formData.criteres_recherche?.budget_max;

  // ==================== RENDU ====================
  
  return (
    <div className="bg-slate-50 min-h-screen pb-24">
      {/* Header fixe avec NavigationBar intégrée - RESPONSIVE */}
      {/* lot DK2 — telephone : l'en-tete defile, seule la barre de sections reste collee */}
      <div className="md:sticky md:top-0 z-50 bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-3 md:px-6 py-3 md:py-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-0">
            <div className="flex items-center gap-2 md:gap-4 w-full md:w-auto">
              <button 
                onClick={onCancel}
                className="text-slate-600 hover:text-slate-800 transition flex-shrink-0"
              >
                <i className="bi bi-arrow-left text-lg md:text-xl"></i>
              </button>
              
              {/* Photo de profil */}
              <div className="relative group flex-shrink-0">
                <input
                  type="file"
                  id="profile-photo-input"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      // Preview immédiat
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setProfilePhoto(reader.result);
                      };
                      reader.readAsDataURL(file);
                      
                      // Upload vers le backend
                      handlePhotoUpload(file);
                    }
                  }}
                />
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-xl md:text-2xl font-bold shadow-lg overflow-hidden">
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Photo de profil" className="w-full h-full object-cover" />
                  ) : (
                    formData.nom ? formData.nom.charAt(0).toUpperCase() : '?'
                  )}
                </div>
                {profilePhoto ? (
                  <button
                    type="button"
                    onClick={handlePhotoDelete}
                    className="absolute inset-0 w-16 h-16 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center cursor-pointer"
                    title="Supprimer la photo"
                  >
                    <i className="bi bi-trash text-white text-xl"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => document.getElementById('profile-photo-input').click()}
                    className="absolute inset-0 w-16 h-16 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center cursor-pointer"
                    title="Ajouter une photo"
                  >
                    <i className="bi bi-camera text-white text-xl"></i>
                  </button>
                )}
              </div>
              
              <div className="min-w-0 flex-1">
                <h1 className="text-lg md:text-2xl font-bold text-slate-800 truncate">{displayName}</h1>
                <p className="text-xs md:text-sm text-slate-500">Fiche contact complète</p>
              </div>
            </div>
            <div className="hidden md:flex flex-col sm:flex-row gap-2 md:gap-3 w-full md:w-auto">
              {/* Bouton « Assistant Vocal » retiré (démo-ready) — feature non implémentée. */}
              <button 
                type="button"
                onClick={onCancel}
                className="px-4 md:px-5 py-2 md:py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold transition text-sm md:text-base"
              >
                Annuler
              </button>
              {!isNew && onView && (
                <button 
                  type="button"
                  onClick={onView}
                  className="px-4 md:px-5 py-2 md:py-2.5 bg-[#EFF6FF] hover:bg-blue-200 text-[#007BFF] rounded-lg font-semibold transition text-sm md:text-base"
                >
                  <i className="bi bi-eye mr-2"></i>
                  Consulter
                </button>
              )}
              <button 
                onClick={handleSubmit}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-[#10B981] hover:bg-[#059669] text-white rounded-lg font-bold shadow-md transition text-sm md:text-base"
              >
                <i className="bi bi-check-circle mr-2"></i>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* lot DK2 — barre de sections collee en haut (telephone comme ordinateur) */}
      <div className="sticky top-0 z-40">
        <NavigationBar sections={sectionsVisibles} />
      </div>

      <div className="max-w-7xl mx-auto p-3 md:p-6 flex flex-col lg:flex-row gap-4 md:gap-6">
        {/* Contenu principal */}
        <main className="flex-1 space-y-4 md:space-y-6 w-full lg:w-auto">
          <form onSubmit={handleSubmit}>
            <IdentitySection 
              formData={formData}
              onChange={handleChange}
              userProfile={userProfile}
              onAddressSearch={searchAddress}
              addressSuggestions={addressSuggestions}
              showAddressSuggestions={showAddressSuggestions}
              onSelectAddress={selectAddress}
            />

            {/* Sections Recherche, Financier et Biens - Uniquement si profil acquéreur */}
            {isContactAcquireur(formData) && (
              <>
                {/* Système de multiples recherches */}
                <MultipleSearchesManager 
                  recherches={formData.recherches || []}
                  onUpdate={handleUpdateRecherches}
                  onToggleEquipement={toggleEquipement}
                />

                <FinancialSection 
                  formData={formData}
                  onChange={handleConfidentielChange}
                  ratio={ratio}
                />

                {!isNew && <PropertiesSection />}
              </>
            )}

            {!isNew && (
              <HistoriqueSection 
                contactId={contact?.id} 
                refreshTrigger={historyRefreshTrigger}
                onHistoryChange={reloadContactData}
              />
            )}

            <ActionsBar 
              onCancel={onCancel}
              onGenerateEmail={() => setShowEmailModal(true)}
              canDelete={!!contact}
              canGenerateEmail={!!contact}
            />
          </form>
        </main>

        {/* Widgets IA */}
        <AIWidgets 
          formData={formData}
          resume={resume}
          score={score}
          hasApport={hasApport}
          hasBudget={hasBudget}
          contactId={contact?.id}
          onRegenerateSummary={(summary) => {
            console.log('🤖 Résumé IA généré:', summary);
            setAiSummary(summary);
            // Note: Le résumé est déjà sauvegardé côté backend, pas besoin de recharger
          }}
        />
      </div>

      {/* lot DK2 — telephone : enregistrement en bas, au-dessus de la barre du bas */}
      <div className="md:hidden" aria-hidden="true"
           style={{ height: 'calc(96px + env(safe-area-inset-bottom, 0px))' }} />
      <div
        className="md:hidden fixed inset-x-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex gap-3"
        style={{ bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))' }}
        data-testid="contact-form-barre-bas"
      >
        <button
          type="button"
          onClick={onCancel}
          data-testid="contact-form-annuler-mobile"
          className="flex-1 h-12 rounded-lg bg-slate-100 text-slate-700 font-semibold"
        >
          Annuler
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          data-testid="contact-form-enregistrer-mobile"
          className="flex-1 h-12 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white font-bold shadow-md"
        >
          <i className="bi bi-check-circle mr-2"></i>
          Enregistrer
        </button>
      </div>

      {/* Modale génération d'email */}
      <EmailGeneratorModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        contactId={contact?.id}
        contactName={displayName}
        onEmailSaved={reloadContactData}
      />
    </div>
  );
};

export default ContactForm;
